import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import KpiTile from './KpiTile';
import LedgerTab from './LedgerTab.js';
import InvoiceTab from './InvoiceTab';
import Icon from './Icon';
import StaleSyncBanner from './StaleSyncBanner';
import { getVisitSchedule } from '../services/paymentsService';

// Schedule tab — the practice's OpenDental appointment book (day grid by
// operatory, or a week of day columns), each visit colored by where its money
// stands after insurance: waiting on a claim, ready to collect, invoiced,
// paid. Every appointment has a "Collect" button (Virtual / Physical terminal)
// that runs the Invoices tab's Collect payment wizard for that family in place
// (a modal-only InvoiceTab), like Dental Intel's schedule → Payments menu.
// Data + state rules: payments/backend/visit_schedule.py.

const STATES = {
  ready: { label: 'Ready to collect', fg: '#1d6b40', bg: '#e8f5ee', border: '#34a065' },
  waiting: { label: 'Waiting on insurance', fg: '#8a5a00', bg: '#fdf3e1', border: '#e0a63a' },
  invoiced: { label: 'Invoiced', fg: '#1f4f9c', bg: '#e8effb', border: '#5b86d6' },
  on_plan: { label: 'On payment plan', fg: '#5b3fa8', bg: '#f1ecfb', border: '#9b82d9' },
  paid: { label: 'Paid', fg: '#5f6b64', bg: '#f3f5f4', border: '#c9d1cc' },
  no_charges: { label: 'No charges', fg: '#5f6b64', bg: '#f7f8f7', border: '#d6dbd7' },
  scheduled: { label: 'Scheduled', fg: '#3c4540', bg: '#ffffff', border: '#c9d1cc' },
  broken: { label: 'Broken', fg: '#b42318', bg: '#fdf0ee', border: '#e7a59c' },
};

const FILTERS = [
  ['all', 'All visits'],
  ['ready', 'Ready to collect'],
  ['waiting', 'Waiting on insurance'],
  ['invoiced', 'Invoiced'],
];

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const formatCurrency = (n) => {
  const v = Number(n || 0);
  return v.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
};
// Summary tiles: whole dollars from $1,000 up so the figure fits the tile.
const formatKpiCurrency = (n) => {
  const v = Number(n || 0);
  return Math.abs(v) >= 1000
    ? v.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 })
    : formatCurrency(v);
};

// Below this viewport width the visit drawer overlays the schedule instead
// of reserving room beside it (which would crush the summary tiles).
const DRAWER_SIDE_BY_SIDE_MIN_WIDTH = 1280;
const useViewportWidth = () => {
  const [width, setWidth] = useState(() => (typeof window === 'undefined' ? 1440 : window.innerWidth));
  useEffect(() => {
    const onResize = () => setWidth(window.innerWidth);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  return width;
};

// Dates are handled as 'YYYY-MM-DD' strings in practice-local time; parse them
// as UTC midnights so browser timezone never shifts a day.
const parseDay = (s) => {
  const [y, m, d] = s.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d));
};
const fmtDay = (dt) => dt.toISOString().slice(0, 10);
const addDays = (s, n) => {
  const dt = parseDay(s);
  dt.setUTCDate(dt.getUTCDate() + n);
  return fmtDay(dt);
};
const mondayOf = (s) => {
  const dow = parseDay(s).getUTCDay();
  return addDays(s, dow === 0 ? -6 : 1 - dow);
};
const localToday = () => {
  const now = new Date();
  return fmtDay(new Date(Date.UTC(now.getFullYear(), now.getMonth(), now.getDate())));
};
// Minutes past midnight right now in the practice's timezone.
const nowMinutesIn = (timeZone) => {
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: timeZone || undefined, hour: '2-digit', minute: '2-digit', hourCycle: 'h23',
    }).formatToParts(new Date());
    const get = (t) => Number(parts.find((p) => p.type === t)?.value || 0);
    return get('hour') * 60 + get('minute');
  } catch (e) {
    const d = new Date();
    return d.getHours() * 60 + d.getMinutes();
  }
};

const dayLabel = (s, { long = false } = {}) => {
  const dt = parseDay(s);
  const wd = WEEKDAYS[dt.getUTCDay()];
  const md = `${MONTHS[dt.getUTCMonth()]} ${dt.getUTCDate()}`;
  return long ? `${wd}, ${md}, ${dt.getUTCFullYear()}` : `${wd} ${md}`;
};
const timeLabel = (hhmm) => {
  if (!hhmm) return '';
  const [h, m] = hhmm.split(':').map(Number);
  const suffix = h >= 12 ? 'p' : 'a';
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return `${h12}:${String(m).padStart(2, '0')}${suffix}`;
};
const minutesOf = (hhmm) => {
  const [h, m] = (hhmm || '0:0').split(':').map(Number);
  return h * 60 + m;
};
const shortDate = (iso) => {
  if (!iso) return '';
  const s = String(iso).slice(0, 10);
  const dt = parseDay(s);
  return `${MONTHS[dt.getUTCMonth()]} ${dt.getUTCDate()}`;
};

// The amount a card shows: the family balance for anything collectible.
// Siblings each show the same family balance, so mark it as such.
const cardAmount = (v) => {
  if (v.state === 'ready' || v.state === 'invoiced' || v.state === 'on_plan') {
    const amount = formatCurrency(v.household.balance);
    return v.household.memberCount > 1 ? `${amount} fam.` : amount;
  }
  if (v.state === 'waiting' && v.household.pendingInsurance > 0) {
    return `${formatCurrency(v.household.pendingInsurance)} ins.`;
  }
  return null;
};

const StateChip = ({ state, small = false }) => {
  const s = STATES[state] || STATES.scheduled;
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 5,
        padding: small ? '1px 6px' : '2px 8px',
        borderRadius: 999,
        fontSize: small ? '0.68rem' : '0.75rem',
        fontWeight: 600,
        color: s.fg,
        background: s.bg,
        border: `1px solid ${s.border}`,
        whiteSpace: 'nowrap',
      }}
    >
      <span style={{ width: 6, height: 6, borderRadius: 999, background: s.border }} />
      {s.label}
    </span>
  );
};

// Opens the Virtual / Physical terminal menu, anchored to the button.
const CollectButton = ({ onClick, compact = false }) => (
  <button
    type="button"
    className="pm-btn pm-btn-sm pm-btn-primary"
    style={compact ? { height: 20, minHeight: 0, padding: '0 7px', fontSize: '0.68rem', lineHeight: 1, flexShrink: 0 } : undefined}
    aria-haspopup="menu"
    onClick={(e) => {
      e.stopPropagation();
      onClick(e.currentTarget.getBoundingClientRect());
    }}
  >
    Collect
  </button>
);

const COLLECT_METHODS = [
  { id: 'virtual', icon: 'credit-card', label: 'Virtual terminal' },
  { id: 'physical', icon: 'smartphone', label: 'Physical terminal' },
];

const MENU_WIDTH = 200;
// { visit, rect } → the method menu next to the Collect button that opened it.
const CollectMenu = ({ menu, onPick, onClose }) => {
  if (!menu) return null;
  const { rect } = menu;
  const left = Math.max(8, Math.min(rect.right - MENU_WIDTH, window.innerWidth - MENU_WIDTH - 8));
  const below = rect.bottom + 6;
  const top = below + 96 > window.innerHeight ? Math.max(8, rect.top - 96 - 6) : below;
  return (
    <>
      <div className="pm-menu-backdrop" onClick={onClose} />
      <div role="menu" aria-label="Collect payment" className="pm-menu" style={{ position: 'fixed', top, left, width: MENU_WIDTH }}>
        {COLLECT_METHODS.map((m) => (
          <button key={m.id} type="button" role="menuitem" className="pm-menu-item" onClick={() => onPick(m.id)}>
            <Icon name={m.icon} size={15} />
            <span className="pm-menu-item-text">{m.label}</span>
          </button>
        ))}
      </div>
    </>
  );
};

// Side-by-side lanes for double-booked appointments in one operatory column.
// Lanes are per overlap cluster (appointments that transitively overlap), so
// one double-booking doesn't narrow every other card in the column.
const layoutLanes = (visits) => {
  const sorted = [...visits].sort((a, b) => minutesOf(a.time) - minutesOf(b.time));
  const placed = [];
  let cluster = [];
  let clusterEnd = -1;
  let laneEnds = [];
  const flush = () => {
    const lanes = Math.max(laneEnds.length, 1);
    for (const p of cluster) placed.push({ ...p, lanes });
    cluster = [];
    laneEnds = [];
  };
  for (const v of sorted) {
    const start = minutesOf(v.time);
    if (cluster.length && start >= clusterEnd) flush();
    let lane = laneEnds.findIndex((end) => end <= start);
    if (lane === -1) {
      lane = laneEnds.length;
      laneEnds.push(0);
    }
    laneEnds[lane] = start + v.minutes;
    clusterEnd = Math.max(cluster.length ? clusterEnd : 0, start + v.minutes);
    cluster.push({ v, lane });
  }
  flush();
  return placed;
};

// Vertical scale of the day grid's time axis per density (px per minute),
// matching the main app's schedule board.
const DENSITY = { comfortable: 2.4, compact: 1.5 };
// Breathing room above the first hour so its label isn't clipped.
const GRID_TOP_PAD = 10;
const GUTTER_W = 64;
const COL_MIN_W = 200;

// "8 AM" for the axis.
const hourLabel = (minutes) => {
  const h = Math.floor(minutes / 60) % 24;
  return `${h % 12 || 12} ${h >= 12 ? 'PM' : 'AM'}`;
};
// "8:00 – 9:00 AM" when both ends share a meridiem, else "11:30 AM – 12:30 PM".
const clock = (minutes) => {
  const h = Math.floor(minutes / 60) % 24;
  return { text: `${h % 12 || 12}:${String(minutes % 60).padStart(2, '0')}`, ampm: h >= 12 ? 'PM' : 'AM' };
};
const rangeLabel = (startMin, endMin) => {
  const a = clock(startMin);
  const b = clock(endMin);
  return a.ampm === b.ampm ? `${a.text} – ${b.text} ${b.ampm}` : `${a.text} ${a.ampm} – ${b.text} ${b.ampm}`;
};

// What fits a block of this height (same tiers as the main app's board): one
// line; time + name; + the procedure line; + the state chip.
const cardTier = (h) => (h < 36 ? 'line' : h < 66 ? 'name' : h < 92 ? 'type' : 'full');

const DayCard = ({ v, height, narrow, dim, isSel, onSelect, onCollect }) => {
  const s = STATES[v.state] || STATES.scheduled;
  const start = minutesOf(v.time);
  const range = rangeLabel(start, start + v.minutes);
  // Narrow cards keep only the money to collect (no "$X ins." estimate).
  const amount = narrow && v.state === 'waiting' ? null : cardAmount(v);
  const tier = cardTier(height);
  // Side-by-side (double-booked) cards are too narrow for the inline button;
  // the visit drawer still has Collect payment.
  const canCollect = v.state !== 'broken' && !narrow;
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => onSelect(v)}
      onKeyDown={(e) => { if (e.key === 'Enter') onSelect(v); }}
      title={[range, v.patient.name, s.label].join(' · ')}
      style={{
        height: '100%',
        boxSizing: 'border-box',
        background: s.bg,
        border: `1px solid ${isSel ? '#16241c' : s.border}`,
        borderLeft: `3px solid ${s.border}`,
        borderRadius: 7,
        padding: tier === 'line' ? '1px 6px' : '3px 7px',
        overflow: 'hidden',
        cursor: 'pointer',
        opacity: dim ? 0.3 : 1,
        boxShadow: isSel ? '0 0 0 3px rgba(22,36,28,0.12)' : 'none',
        color: '#16241c',
        lineHeight: 1.3,
      }}
    >
      {tier === 'line' ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.7rem', whiteSpace: 'nowrap' }}>
          <span style={{ color: '#6b716d', fontVariantNumeric: 'tabular-nums' }}>{timeLabel(v.time)}</span>
          <span data-phi style={{ fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', textDecoration: v.state === 'broken' ? 'line-through' : 'none' }}>
            {v.patient.name}
          </span>
          {amount && <span style={{ marginLeft: 'auto', color: s.fg, fontWeight: 700 }}>{amount}</span>}
        </div>
      ) : (
        <>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 6, fontSize: '0.68rem', color: '#6b716d', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{narrow ? timeLabel(v.time) : range}</span>
            {amount && <span style={{ color: s.fg, fontWeight: 700 }}>{amount}</span>}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 6, marginTop: 1 }}>
            <span
              data-phi
              style={{
                fontSize: '0.8rem', fontWeight: 700, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                textDecoration: v.state === 'broken' ? 'line-through' : 'none',
              }}
            >
              {v.patient.name}
            </span>
            {canCollect && tier !== 'full' && <CollectButton compact onClick={(rect) => onCollect(v, rect)} />}
          </div>
          {(tier === 'type' || tier === 'full') && (
            <div style={{ fontSize: '0.72rem', color: '#59615c', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', marginTop: 1 }}>
              {v.procDescript || '\u00a0'}
            </div>
          )}
          {tier === 'full' && (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 6, marginTop: 4 }}>
              {narrow ? <span style={{ fontSize: '0.7rem', color: s.fg, fontWeight: 600 }}>{s.label}</span> : <StateChip state={v.state} small />}
              {canCollect && <CollectButton compact onClick={(rect) => onCollect(v, rect)} />}
            </div>
          )}
        </>
      )}
    </div>
  );
};

const DayGrid = ({ visits, operatories, filter, selected, onSelect, onCollect, density, nowMinutes }) => {
  const ops = useMemo(() => {
    const counts = {};
    for (const v of visits) counts[String(v.op)] = (counts[String(v.op)] || 0) + 1;
    return operatories
      .filter((o) => counts[String(o.id)])
      .map((o) => ({ ...o, count: counts[String(o.id)] }));
  }, [visits, operatories]);

  if (!visits.length) {
    return <div className="pm-empty"><div className="pm-empty-title">No appointments on this day</div></div>;
  }

  const pxPerMin = DENSITY[density] || DENSITY.comfortable;
  // The axis spans whole hours around the day's first start and last end.
  const first = Math.min(...visits.map((v) => minutesOf(v.time)));
  const last = Math.max(...visits.map((v) => minutesOf(v.time) + v.minutes));
  const dayStart = Math.floor(first / 60) * 60;
  const dayEnd = Math.max(Math.ceil(last / 60) * 60, dayStart + 60);
  const yFor = (m) => GRID_TOP_PAD + (m - dayStart) * pxPerMin;
  const gridHeight = (dayEnd - dayStart) * pxPerMin + GRID_TOP_PAD * 2;
  const halfHours = [];
  for (let m = dayStart; m <= dayEnd; m += 30) halfHours.push(m);
  const showNow = nowMinutes != null && nowMinutes >= dayStart && nowMinutes <= dayEnd;
  const surface = '#fbfcfd';

  return (
    <div
      className="pm-card"
      data-testid="day-grid"
      style={{
        padding: 0, overflow: 'auto', background: surface,
        maxHeight: 'calc(100vh - 230px)', minHeight: 320,
      }}
    >
      <div style={{ minWidth: GUTTER_W + ops.length * (COL_MIN_W + 10), paddingRight: 12, paddingBottom: 8 }}>
        {/* Operatory headers — stay visible while the day scrolls. */}
        <div style={{ position: 'sticky', top: 0, zIndex: 4, display: 'flex', background: surface, paddingTop: 12 }}>
          <div style={{ position: 'sticky', left: 0, zIndex: 1, width: GUTTER_W, flex: `0 0 ${GUTTER_W}px`, background: surface }} />
          <div style={{ display: 'flex', flex: 1, gap: 10 }}>
            {ops.map((op) => (
              <div key={op.id} style={{ flex: 1, minWidth: COL_MIN_W, maxWidth: 320 }}>
                <div
                  title={op.name}
                  style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 6,
                    padding: '0 4px 8px', borderBottom: '1px solid #e3e7e4',
                  }}
                >
                  <span style={{
                    fontSize: '0.7rem', fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase',
                    color: '#59615c', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                  }}
                  >
                    {op.name}
                  </span>
                  <span style={{ fontSize: '0.7rem', color: '#8a918c', fontVariantNumeric: 'tabular-nums' }}>{op.count}</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div style={{ position: 'relative', display: 'flex', height: gridHeight }}>
          {/* Hour (solid) and half-hour (dashed) rules across every column. */}
          {halfHours.map((m) => (
            <div
              key={m}
              aria-hidden="true"
              style={{
                position: 'absolute', left: GUTTER_W, right: 0, top: yFor(m), pointerEvents: 'none',
                borderTop: m % 60 === 0 ? '1px solid #e3e7e4' : '1px dashed #eef1ef',
              }}
            />
          ))}

          {/* Time gutter — stays put while scrolling sideways. */}
          <div style={{ position: 'sticky', left: 0, zIndex: 3, width: GUTTER_W, flex: `0 0 ${GUTTER_W}px`, background: surface }}>
            {halfHours.filter((m) => m % 60 === 0).map((m) => (
              <span
                key={m}
                style={{
                  position: 'absolute', right: 10, top: yFor(m), transform: 'translateY(-50%)',
                  fontSize: '0.7rem', color: '#8a918c', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap',
                }}
              >
                {hourLabel(m)}
              </span>
            ))}
            {showNow && (
              <span
                style={{
                  position: 'absolute', right: 4, top: yFor(nowMinutes), transform: 'translateY(-50%)', zIndex: 1,
                  fontSize: '0.66rem', fontWeight: 700, color: '#fff', background: '#d93a3a',
                  borderRadius: 999, padding: '1px 6px', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap',
                }}
              >
                {timeLabel(`${Math.floor(nowMinutes / 60)}:${nowMinutes % 60}`)}
              </span>
            )}
          </div>

          {/* Current time across the columns when viewing today. */}
          {showNow && (
            <div
              aria-hidden="true"
              style={{
                position: 'absolute', left: GUTTER_W, right: 0, top: yFor(nowMinutes), zIndex: 2,
                borderTop: '2px solid #d93a3a', pointerEvents: 'none',
              }}
            />
          )}

          <div style={{ display: 'flex', flex: 1, gap: 10 }}>
            {ops.map((op) => {
              const placed = layoutLanes(visits.filter((v) => String(v.op) === String(op.id)));
              return (
                <div key={op.id} style={{ position: 'relative', flex: 1, minWidth: COL_MIN_W, maxWidth: 320 }}>
                  {placed.map(({ v, lane, lanes }) => {
                    const isSel = !!selected && selected.aptNum === v.aptNum;
                    // 1px inset each side so back-to-back appointments read as separate blocks.
                    const height = Math.max(v.minutes * pxPerMin - 2, 16);
                    return (
                      <div
                        key={v.aptNum}
                        data-testid={`apt-${v.aptNum}`}
                        style={{
                          position: 'absolute',
                          top: yFor(minutesOf(v.time)) + 1,
                          height,
                          left: `${(lane / lanes) * 100}%`,
                          width: `${100 / lanes}%`,
                          padding: '0 1px',
                          boxSizing: 'border-box',
                          zIndex: isSel ? 1 : 'auto',
                        }}
                      >
                        <DayCard
                          v={v}
                          height={height}
                          narrow={lanes > 1}
                          dim={filter !== 'all' && v.state !== filter}
                          isSel={isSel}
                          onSelect={onSelect}
                          onCollect={onCollect}
                        />
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

const WeekColumns = ({ days, visitsByDay, summaries, filter, selected, onSelect, onCollect, onOpenDay, today }) => (
  <div style={{ display: 'grid', gridTemplateColumns: `repeat(${days.length}, minmax(180px, 1fr))`, gap: 10, overflowX: 'auto' }}>
    {days.map((d) => {
      const list = (visitsByDay[d] || []).filter((v) => filter === 'all' || v.state === filter);
      const sum = summaries[d] || {};
      return (
        <div key={d} className="pm-card" style={{ padding: 0, minWidth: 0 }}>
          <button
            type="button"
            onClick={() => onOpenDay(d)}
            title="Open day view"
            style={{
              display: 'block', width: '100%', textAlign: 'left', cursor: 'pointer',
              padding: '10px 12px', background: d === today ? '#f2f8f4' : '#fafbfa',
              border: 'none', borderBottom: '1px solid #e3e7e4', borderRadius: '12px 12px 0 0',
            }}
          >
            <div style={{ fontWeight: 700, color: '#16241c', fontSize: '0.88rem' }}>{dayLabel(d)}</div>
            <div style={{ fontSize: '0.74rem', color: '#6b716d', marginTop: 2 }}>
              {sum.visits || 0} visits
              {sum.ready ? (
                <span style={{ color: STATES.ready.fg, fontWeight: 700 }}>
                  {' · '}{sum.ready} ready {formatCurrency(sum.readyAmount)}
                </span>
              ) : null}
              {sum.waiting ? <span style={{ color: STATES.waiting.fg }}>{' · '}{sum.waiting} waiting</span> : null}
            </div>
          </button>
          <div style={{ padding: 8, display: 'flex', flexDirection: 'column', gap: 6 }}>
            {list.length === 0 && (
              <div style={{ fontSize: '0.75rem', color: '#8a918c', padding: '6px 4px' }}>
                {(visitsByDay[d] || []).length ? 'None match this filter' : 'No appointments'}
              </div>
            )}
            {list.map((v) => {
              const s = STATES[v.state] || STATES.scheduled;
              const isSel = selected && selected.aptNum === v.aptNum;
              const amount = cardAmount(v);
              return (
                <div
                  key={v.aptNum}
                  role="button"
                  tabIndex={0}
                  onClick={() => onSelect(v)}
                  onKeyDown={(e) => { if (e.key === 'Enter') onSelect(v); }}
                  style={{
                    background: s.bg,
                    border: `1px solid ${isSel ? '#16241c' : s.border}`,
                    borderLeftWidth: 4,
                    borderRadius: 6,
                    padding: '5px 8px',
                    cursor: 'pointer',
                    fontSize: '0.76rem',
                    lineHeight: 1.35,
                    textDecoration: v.state === 'broken' ? 'line-through' : 'none',
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 6 }}>
                    <span style={{ fontWeight: 700, color: '#16241c', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      <span style={{ color: '#6b716d', fontWeight: 500 }}>{timeLabel(v.time)}</span>{' '}
                      <span data-phi>{v.patient.name}</span>
                    </span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 6, marginTop: 2 }}>
                    <span style={{ color: s.fg, fontWeight: 600, whiteSpace: 'nowrap' }}>{amount || s.label}</span>
                    {v.state !== 'broken' && <CollectButton compact onClick={(rect) => onCollect(v, rect)} />}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      );
    })}
  </div>
);

const DetailRow = ({ label, children }) => (
  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, padding: '5px 0', fontSize: '0.84rem' }}>
    <span style={{ color: '#6b716d' }}>{label}</span>
    <span style={{ color: '#16241c', fontWeight: 600, textAlign: 'right' }}>{children}</span>
  </div>
);

const VisitDrawer = ({ visit, operatoryName, onClose, onCollect, onOpenLedger }) => {
  if (!visit) return null;
  const hh = visit.household;
  return (
    <aside
      style={{
        position: 'fixed', top: 0, right: 0, bottom: 0, width: 380, maxWidth: '100vw',
        background: '#fff', borderLeft: '1px solid #e3e7e4', boxShadow: '-8px 0 24px rgba(22,36,28,0.08)',
        zIndex: 40, display: 'flex', flexDirection: 'column', textAlign: 'left',
      }}
      aria-label="Visit details"
    >
      <div style={{ padding: '16px 18px', borderBottom: '1px solid #eef1ef', display: 'flex', justifyContent: 'space-between', gap: 12 }}>
        <div style={{ minWidth: 0 }}>
          <div data-phi style={{ fontSize: '1.05rem', fontWeight: 700, color: '#16241c' }}>{visit.patient.name}</div>
          <div style={{ fontSize: '0.8rem', color: '#6b716d', marginTop: 2 }}>
            {dayLabel(visit.date, { long: true })} · {timeLabel(visit.time)} · {visit.minutes} min
          </div>
          <div style={{ fontSize: '0.8rem', color: '#6b716d' }}>
            {[operatoryName, visit.provider, visit.isNewPatient ? 'New patient' : null].filter(Boolean).join(' · ')}
          </div>
        </div>
        <button type="button" className="pm-icon-btn" aria-label="Close" onClick={onClose}>✕</button>
      </div>

      <div style={{ padding: '14px 18px', overflowY: 'auto', flex: 1 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <StateChip state={visit.state} />
          <span style={{ fontSize: '0.8rem', color: '#59615c' }}>{visit.reason}</span>
        </div>

        <div style={{ marginTop: 16 }}>
          <div style={{ fontSize: '0.72rem', fontWeight: 700, color: '#6b716d', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Visit</div>
          {visit.procDescript && <DetailRow label="Scheduled for">{visit.procDescript}</DetailRow>}
          <DetailRow label="Procedures posted">
            {visit.procedures.length ? `${visit.procedures.length} · ${formatCurrency(visit.fees)}` : 'None'}
          </DetailRow>
          {visit.procedures.some((p) => p.code || p.description) && (
            <div style={{ margin: '2px 0 6px', fontSize: '0.78rem', color: '#59615c' }}>
              {visit.procedures.map((p, i) => (
                <div key={i} style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span>{[p.code, p.description].filter(Boolean).join(' — ') || 'Procedure'}</span>
                  <span>{formatCurrency(p.fee)}</span>
                </div>
              ))}
            </div>
          )}
          <DetailRow label="Insurance paid">
            {visit.claims.length ? formatCurrency(visit.insurancePaid) : 'No claim (self-pay)'}
          </DetailRow>
          {visit.settledOn && <DetailRow label="Insurance paid on">{shortDate(visit.settledOn)}</DetailRow>}
        </div>

        {visit.claims.length > 0 && (
          <div style={{ marginTop: 14 }}>
            <div style={{ fontSize: '0.72rem', fontWeight: 700, color: '#6b716d', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Claims</div>
            {visit.claims.map((c) => (
              <div key={c.id} style={{ display: 'flex', justifyContent: 'space-between', gap: 10, padding: '6px 0', borderBottom: '1px solid #f1f3f2', fontSize: '0.8rem' }}>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontWeight: 600, color: '#16241c' }}>{c.carrier || 'Carrier'} <span style={{ color: '#8a918c', fontWeight: 400 }}>#{c.id}</span></div>
                  <div style={{ color: c.state === 'PAID' ? STATES.ready.fg : STATES.waiting.fg }}>
                    {c.state === 'PAID' ? `Paid${c.paidOn ? ` ${shortDate(c.paidOn)}` : ''}` : c.state === 'CREATED' ? 'Not sent' : 'Submitted'}
                  </div>
                </div>
                <div style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                  <div>{formatCurrency(c.insurancePaid)} <span style={{ color: '#8a918c' }}>of {formatCurrency(c.billed)}</span></div>
                </div>
              </div>
            ))}
          </div>
        )}

        <div style={{ marginTop: 16 }}>
          <div style={{ fontSize: '0.72rem', fontWeight: 700, color: '#6b716d', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
            Family account{hh.memberCount > 1 ? ` · ${hh.memberCount} patients` : ''}
          </div>
          <DetailRow label="Guarantor"><span data-phi>{hh.name}</span></DetailRow>
          <DetailRow label="Family balance">
            <span style={{ color: hh.balance > 0 ? '#16241c' : '#5f6b64' }}>{formatCurrency(hh.balance)}</span>
          </DetailRow>
          {hh.pendingClaims > 0 && (
            <DetailRow label="Insurance still pending">
              {formatCurrency(hh.pendingInsurance)} · {hh.pendingClaims} claim{hh.pendingClaims === 1 ? '' : 's'}
            </DetailRow>
          )}
          {visit.invoice && (
            <DetailRow label="Invoice">
              {formatCurrency(visit.invoice.total)} · {visit.invoice.status} · {shortDate(visit.invoice.createdAt)}
            </DetailRow>
          )}
          {visit.onPlan && <DetailRow label="Payment plan">Covers the balance</DetailRow>}
        </div>
      </div>

      <div style={{ padding: '12px 18px', borderTop: '1px solid #eef1ef', display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
        <button type="button" className="pm-btn" onClick={() => onOpenLedger(visit)}>View ledger</button>
        {visit.state !== 'broken' && (
          <button
            type="button"
            className="pm-btn pm-btn-primary"
            aria-haspopup="menu"
            onClick={(e) => onCollect(visit, e.currentTarget.getBoundingClientRect())}
          >
            Collect payment
          </button>
        )}
      </div>
    </aside>
  );
};

const ScheduleTab = ({ practiceId }) => {
  // Opens on today's appointment book, where the front desk collects.
  const [view, setView] = useState('day');
  const [anchor, setAnchor] = useState(localToday);
  const [filter, setFilter] = useState('all');
  const [density, setDensity] = useState('comfortable');
  const [schedule, setSchedule] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selected, setSelected] = useState(null);
  // { contact_id, name, _ts } while the headless ledger statement is open.
  const [ledgerFor, setLedgerFor] = useState(null);
  // { visit, rect } while the Virtual / Physical terminal menu is open.
  const [collectMenu, setCollectMenu] = useState(null);
  // { method, visit, _ts } while the Collect payment wizard (and its
  // waiting-for-payment window) is open.
  const [collectFor, setCollectFor] = useState(null);
  const requestRef = useRef(0);

  // Ticks once a minute while the day view shows today, for the now-line.
  const [nowMinutes, setNowMinutes] = useState(null);
  const showingToday = view === 'day' && !!schedule && anchor === schedule.today;
  useEffect(() => {
    if (!showingToday) {
      setNowMinutes(null);
      return undefined;
    }
    const tick = () => setNowMinutes(nowMinutesIn(schedule.timezone));
    tick();
    const id = setInterval(tick, 60 * 1000);
    return () => clearInterval(id);
  }, [showingToday, schedule]);

  const weekStart = mondayOf(anchor);
  const weekEnd = addDays(weekStart, 6);
  const today = schedule?.today || localToday();

  const load = useCallback(async () => {
    if (!practiceId) return;
    const reqId = ++requestRef.current;
    setLoading(true);
    setError(null);
    try {
      const resp = await getVisitSchedule(practiceId, { start: weekStart, end: weekEnd });
      if (reqId !== requestRef.current) return;
      if (!resp?.success) throw new Error(resp?.error || 'Failed to load schedule');
      setSchedule(resp.schedule);
    } catch (e) {
      if (reqId !== requestRef.current) return;
      setError(e.message || 'Failed to load schedule');
      setSchedule(null);
    } finally {
      if (reqId === requestRef.current) setLoading(false);
    }
  }, [practiceId, weekStart, weekEnd]);

  useEffect(() => {
    load();
  }, [load]);

  // Keep the drawer in sync with refreshed data (e.g. after invoicing).
  useEffect(() => {
    if (!selected || !schedule) return;
    const fresh = schedule.visits.find((v) => v.aptNum === selected.aptNum);
    setSelected(fresh || null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [schedule]);

  // Only trust data for the week on screen: while the next week loads, the
  // previous response must not fill this week's tiles and counts.
  const fresh = !!schedule && schedule.start === weekStart;
  const visits = useMemo(() => (fresh ? schedule.visits : []), [fresh, schedule]);
  const operatories = useMemo(() => (fresh ? schedule.operatories : []), [fresh, schedule]);
  const viewportWidth = useViewportWidth();
  const opName = useMemo(() => {
    const m = {};
    for (const o of operatories) m[String(o.id)] = o.name;
    return m;
  }, [operatories]);

  const visitsByDay = useMemo(() => {
    const out = {};
    for (const v of visits) (out[v.date] = out[v.date] || []).push(v);
    return out;
  }, [visits]);

  // Weekdays always; Sat/Sun only when the practice saw patients that day.
  const weekDays = useMemo(() => {
    const out = [];
    for (let i = 0; i < 7; i += 1) {
      const d = addDays(weekStart, i);
      const dow = parseDay(d).getUTCDay();
      if ((dow === 0 || dow === 6) && !(visitsByDay[d] || []).length) continue;
      out.push(d);
    }
    return out;
  }, [weekStart, visitsByDay]);

  const scope = useMemo(
    () => (view === 'day' ? (visitsByDay[anchor] || []) : visits),
    [view, visitsByDay, anchor, visits]
  );
  const kpis = useMemo(() => {
    const readyHeads = new Map();
    let waiting = 0;
    let invoiced = 0;
    let paid = 0;
    for (const v of scope) {
      if (v.state === 'ready') readyHeads.set(v.household.headId, v.household.balance);
      else if (v.state === 'waiting') waiting += 1;
      else if (v.state === 'invoiced') invoiced += 1;
      else if (v.state === 'paid') paid += 1;
    }
    let readyAmount = 0;
    readyHeads.forEach((amt) => { readyAmount += amt; });
    return { readyFamilies: readyHeads.size, readyAmount, waiting, invoiced, paid };
  }, [scope]);

  const counts = useMemo(() => {
    const c = { all: scope.length };
    for (const v of scope) c[v.state] = (c[v.state] || 0) + 1;
    return c;
  }, [scope]);

  const step = (dir) => setAnchor((a) => addDays(a, dir * (view === 'day' ? 1 : 7)));
  const openDay = (d) => { setAnchor(d); setView('day'); };

  const openCollectMenu = (v, rect) => setCollectMenu({ visit: v, rect });
  const startCollect = (method) => {
    setCollectFor({ method, visit: collectMenu.visit, _ts: Date.now() });
    setCollectMenu(null);
  };
  const openLedger = (v) => {
    setLedgerFor({ contact_id: v.household.headId, name: v.household.name, _ts: Date.now() });
  };

  const rangeTitle = view === 'day'
    ? dayLabel(anchor, { long: true })
    : `${dayLabel(weekStart)} – ${dayLabel(weekEnd)}, ${parseDay(weekEnd).getUTCFullYear()}`;

  return (
    <div
      style={{
        paddingRight: selected && viewportWidth >= DRAWER_SIDE_BY_SIDE_MIN_WIDTH ? 390 : 0,
        transition: 'padding-right 150ms ease-out',
      }}
    >
      <StaleSyncBanner practiceId={practiceId} />
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 14 }}>
        <div className="pm-segmented" role="group" aria-label="Schedule view">
          {[['day', 'Day'], ['week', 'Week']].map(([id, label]) => (
            <button key={id} type="button" className="pm-segment" aria-pressed={view === id} onClick={() => setView(id)}>
              {label}
            </button>
          ))}
        </div>
        <div className="pm-gr-datenav">
          <button type="button" className="pm-icon-btn" aria-label={view === 'day' ? 'Previous day' : 'Previous week'} onClick={() => step(-1)}>‹</button>
          <input
            type="date"
            className="pm-gr-date"
            value={anchor}
            onChange={(e) => e.target.value && setAnchor(e.target.value)}
            aria-label="Schedule date"
          />
          <button type="button" className="pm-icon-btn" aria-label={view === 'day' ? 'Next day' : 'Next week'} onClick={() => step(1)}>›</button>
        </div>
        <div style={{ fontWeight: 700, color: '#16241c', fontSize: '0.95rem' }}>{rangeTitle}</div>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8, alignItems: 'center' }}>
          {view === 'day' && (
            <div className="pm-segmented" role="group" aria-label="Density">
              {[['comfortable', 'Comfortable'], ['compact', 'Compact']].map(([id, label]) => (
                <button key={id} type="button" className="pm-segment" aria-pressed={density === id} onClick={() => setDensity(id)}>
                  {label}
                </button>
              ))}
            </div>
          )}
          <button type="button" className="pm-btn pm-btn-sm" onClick={() => setAnchor(localToday())}>Today</button>
          <button type="button" className="pm-btn pm-btn-sm" onClick={load} disabled={loading}>
            {loading ? 'Loading…' : '↻ Refresh'}
          </button>
        </div>
      </div>

      <div className="pm-kpis">
        <KpiTile
          label="Ready to collect"
          value={fresh ? formatKpiCurrency(kpis.readyAmount) : '—'}
          valueColor={STATES.ready.fg}
          sub={fresh ? `${kpis.readyFamilies} famil${kpis.readyFamilies === 1 ? 'y' : 'ies'} · insurance settled` : 'Loading…'}
          onClick={() => setFilter('ready')}
        />
        <KpiTile
          label="Waiting on insurance"
          value={fresh ? kpis.waiting : '—'}
          sub="visits with a claim still out"
          onClick={() => setFilter('waiting')}
        />
        <KpiTile label="Invoiced" value={fresh ? kpis.invoiced : '—'} sub="open invoice sent" onClick={() => setFilter('invoiced')} />
        <KpiTile label="Paid" value={fresh ? kpis.paid : '—'} sub="nothing owed" />
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginBottom: 12 }}>
        <div className="pm-segmented" role="group" aria-label="Filter visits">
          {FILTERS.map(([id, label]) => (
            <button key={id} type="button" className="pm-segment" aria-pressed={filter === id} onClick={() => setFilter(id)}>
              {label}
              <span className="pm-segment-count">{counts[id] || 0}</span>
            </button>
          ))}
        </div>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', fontSize: '0.72rem', color: '#6b716d' }}>
          {['ready', 'waiting', 'invoiced', 'on_plan', 'paid', 'scheduled', 'broken'].map((k) => (
            <span key={k} style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
              <span style={{ width: 10, height: 10, borderRadius: 3, background: STATES[k].bg, border: `1px solid ${STATES[k].border}` }} />
              {STATES[k].label}
            </span>
          ))}
        </div>
      </div>

      {error && (
        <div className="pm-gr-error" style={{ marginBottom: 16 }}>
          {error} <button type="button" className="pm-btn pm-btn-sm" onClick={load}>Retry</button>
        </div>
      )}

      {!fresh && loading && (
        <div className="pm-empty"><div className="pm-empty-title">Loading schedule…</div></div>
      )}

      {fresh && (
        <div style={{ opacity: loading ? 0.6 : 1, transition: 'opacity 150ms ease-out' }}>
          {view === 'day' ? (
            <DayGrid
              visits={visitsByDay[anchor] || []}
              operatories={operatories}
              filter={filter}
              selected={selected}
              onSelect={setSelected}
              onCollect={openCollectMenu}
              density={density}
              nowMinutes={nowMinutes}
            />
          ) : (
            <WeekColumns
              days={weekDays}
              visitsByDay={visitsByDay}
              summaries={schedule.days || {}}
              filter={filter}
              selected={selected}
              onSelect={setSelected}
              onCollect={openCollectMenu}
              onOpenDay={openDay}
              today={today}
            />
          )}
        </div>
      )}

      <VisitDrawer
        visit={selected}
        operatoryName={selected ? opName[String(selected.op)] : ''}
        onClose={() => setSelected(null)}
        onCollect={openCollectMenu}
        onOpenLedger={openLedger}
      />

      <CollectMenu menu={collectMenu} onPick={startCollect} onClose={() => setCollectMenu(null)} />

      {/* The Invoices tab's Collect payment wizard (no invoice page), started
          on the visit's family: the guarantor pays, as on the Ledger. It hands
          off to the same waiting-for-payment window; reload when it closes. */}
      {collectFor && (
        <InvoiceTab
          key={collectFor._ts}
          practiceId={practiceId}
          modalOnly
          collectRequest={{
            method: collectFor.method,
            patient: {
              id: collectFor.visit.household.headId,
              patientId: collectFor.visit.household.headId,
              name: collectFor.visit.household.name,
              phoneNumber: collectFor.visit.household.phone || '',
              email: collectFor.visit.household.email || '',
            },
            description: `Payment for ${collectFor.visit.patient.name}`,
          }}
          onClose={() => {
            setCollectFor(null);
            load();
          }}
        />
      )}
      {ledgerFor && (
        <LedgerTab
          key={ledgerFor._ts}
          practiceId={practiceId}
          modalOnly
          initialPatient={{ contact_id: ledgerFor.contact_id, name: ledgerFor.name }}
          onClose={() => setLedgerFor(null)}
        />
      )}
    </div>
  );
};

export default ScheduleTab;
